/**
 * ============================================================================
 * PLATFORM PAGE: Wallet & Ledgers (Administration & Tenant Workspace)
 * ============================================================================
 * Purpose:
 *   Centralized financial management interface supporting role-based boundaries:
 *   - SuperAdmin: Add balance (mint), Transfer balance, Remove balance.
 *                 Can view all roles and all users.
 *   - Admin:      Transfer balance, Remove balance.
 *                 Can view all users, resellers, accounts (except SuperAdmin).
 *   - Reseller:   Transfer balance, Remove balance.
 *                 Can view ONLY their created users.
 *   - Account:    Transfer balance, view all transactions (credit, debit, transfer).
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import { ledgerService } from "../../services";
import {
  getCurrentUser,
  getUserRole,
  isSuperAdmin,
  isAdmin,
  isAccountRole,
} from "../../../core/utils/auth";

// SVG Icons
const MagnifyingGlassIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);

const PlusIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const ArrowsRightLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);

const MinusIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
  </svg>
);

const ArrowPathIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const XMarkIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const ChevronLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
  </svg>
);

export default function LedgerManagement() {
  const currentUser = getCurrentUser();
  const userRole = getUserRole(currentUser);

  // Active Tab
  // Default to 'transactions' if account role, 'users' if superadmin/admin/reseller, 'credits' if client user
  const initialTab = isAccountRole(currentUser)
    ? "transactions"
    : ["superadmin", "admin", "reseller"].includes(userRole)
    ? "users"
    : "credits";

  const [activeTab, setActiveTab] = useState(initialTab);

  // Balance Summary
  const [balanceSummary, setBalanceSummary] = useState({
    totalCredits: 0,
    totalDebits: 0,
    currentBalance: 0,
  });

  // Users & Balances Data
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [requesterMeta, setRequesterMeta] = useState(null);
  const [permissions, setPermissions] = useState({
    canAdd: isSuperAdmin(currentUser) || isAdmin(currentUser),
    canTransfer: ["superadmin", "admin", "reseller", "account"].includes(userRole),
    canRemove: ["superadmin", "admin", "reseller"].includes(userRole),
    canViewAllTransactions: ["superadmin", "account"].includes(userRole),
  });

  // Transactions Audit Data
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [txnSearch, setTxnSearch] = useState("");
  const [txnTypeFilter, setTxnTypeFilter] = useState("all");
  const [txnPage, setTxnPage] = useState(1);
  const [txnLimit, setTxnLimit] = useState(15);
  const [totalTxns, setTotalTxns] = useState(0);

  // Tenant Credit Top-ups State
  const [credits, setCredits] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [creditSearch, setCreditSearch] = useState("");
  const [creditPage, setCreditPage] = useState(1);
  const [creditLimit, setCreditLimit] = useState(10);

  // Tenant Debits State
  const [debits, setDebits] = useState([]);
  const [loadingDebits, setLoadingDebits] = useState(false);
  const [debitSearch, setDebitSearch] = useState("");
  const [debitPage, setDebitPage] = useState(1);
  const [debitLimit, setDebitLimit] = useState(10);

  // Right Slide-Over Drawer State: null | "add" | "transfer" | "remove"
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [submittingDrawer, setSubmittingDrawer] = useState(false);

  // Add Form (SuperAdmin & Admin)
  const [addForm, setAddForm] = useState({
    target_account_id: "",
    target_user_id: "",
    amount: "",
    description: `${userRole === "admin" ? "Admin" : "SuperAdmin"} Wallet Recharge`,
    expire_on: "",
  });

  // Transfer Form (SuperAdmin, Admin, Reseller, Account)
  const [transferForm, setTransferForm] = useState({
    from_account_id: "",
    to_account_id: "",
    amount: "",
    description: "",
  });

  // Remove Form (SuperAdmin, Admin, Reseller)
  const [removeForm, setRemoveForm] = useState({
    target_account_id: "",
    source_account_id: "",
    amount: "",
    return_to_source: true,
    description: "",
  });

  // 1. Load Role-Scoped Users & Balances
  const loadUsersAndBalances = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await ledgerService.getUsersAndBalances({
        username: currentUser?.str_username,
        userRole,
        userId: currentUser?._id || currentUser?.int_user_id,
        accountId: currentUser?.str_account_id,
      });

      if (res && res.success) {
        setUsersList(Array.isArray(res.data) ? res.data : []);
        if (res.permissions) {
          setPermissions({
            ...res.permissions,
            canAdd: res.permissions.canAdd || isSuperAdmin(currentUser) || isAdmin(currentUser),
          });
        }
        if (res.requester) setRequesterMeta(res.requester);
      }
    } catch (err) {
      console.error("Failed to load users & balances:", err);
    } finally {
      setLoadingUsers(false);
    }
  }, [currentUser?.str_username, currentUser?._id, currentUser?.int_user_id, currentUser?.str_account_id, userRole]);

  // 2. Load Central Audit Transactions
  const loadTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const res = await ledgerService.getTransactions({
        page: txnPage,
        limit: txnLimit,
        type: txnTypeFilter !== "all" ? txnTypeFilter : undefined,
        search: txnSearch || undefined,
        userRole,
        username: currentUser?.str_username,
      });

      if (res && res.success) {
        setTransactions(Array.isArray(res.data) ? res.data : []);
        if (res.pagination) setTotalTxns(res.pagination.total || 0);
      }
    } catch (err) {
      console.error("Failed to load transactions:", err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  }, [txnPage, txnLimit, txnTypeFilter, txnSearch, userRole, currentUser?.str_username]);

  // 3. Load Tenant Balance
  const loadBalance = useCallback(async () => {
    try {
      const res = await ledgerService.getBalance();
      if (res && res.success && res.data) {
        setBalanceSummary(res.data);
      }
    } catch (err) {
      console.error("Failed to load balance:", err);
    }
  }, []);

  // 4. Load Tenant Credits
  const loadCredits = useCallback(async () => {
    setLoadingCredits(true);
    try {
      const res = await ledgerService.credits.get({ limit: 500 });
      if (res && res.success) {
        setCredits(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error("Failed to load credits:", err);
    } finally {
      setLoadingCredits(false);
    }
  }, []);

  // 5. Load Tenant Debits
  const loadDebits = useCallback(async () => {
    setLoadingDebits(true);
    try {
      const res = await ledgerService.debits.get({ limit: 500 });
      if (res && res.success) {
        setDebits(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) {
      console.error("Failed to load debits:", err);
    } finally {
      setLoadingDebits(false);
    }
  }, []);

  // Refresh All
  const handleRefresh = async () => {
    await Promise.all([
      loadUsersAndBalances(),
      loadTransactions(),
      loadBalance(),
      loadCredits(),
      loadDebits(),
    ]);
  };

  useEffect(() => {
    loadUsersAndBalances();
    loadTransactions();
    loadBalance();
    loadCredits();
    loadDebits();
  }, [loadUsersAndBalances, loadTransactions, loadBalance, loadCredits, loadDebits]);

  // Close drawer on ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && activeDrawer) {
        setActiveDrawer(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeDrawer]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (activeDrawer) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeDrawer]);

  const closeDrawer = () => {
    if (!submittingDrawer) {
      setActiveDrawer(null);
    }
  };

  // Pre-fill drawer states when opened
  const openAddDrawer = (targetUserOrAccount = null) => {
    let accId = "";
    let userId = "";

    if (targetUserOrAccount) {
      accId = String(targetUserOrAccount.int_account_id || targetUserOrAccount.str_account_id || "");
      userId = String(targetUserOrAccount._id || targetUserOrAccount.str_username || "");
    } else {
      // Default to self if SuperAdmin or Admin
      const myUser =
        usersList.find((u) => u.is_self) ||
        usersList.find((u) => u.str_username === currentUser?.str_username);
      if (myUser) {
        accId = String(myUser.int_account_id || myUser.str_account_id || "");
        userId = String(myUser._id || myUser.str_username || "");
      }
    }

    setAddForm({
      target_account_id: accId,
      target_user_id: userId,
      amount: "",
      description: `${userRole === "admin" ? "Admin" : "SuperAdmin"} Wallet Recharge`,
      expire_on: "",
    });
    setActiveDrawer("add");
  };

  const openTransferDrawer = (targetUserOrAccount = null) => {
    let defaultSource = "";
    const myUser =
      usersList.find((u) => u.is_self) ||
      usersList.find((u) => u.str_username === currentUser?.str_username);

    if (requesterMeta?.account_id) {
      defaultSource = String(requesterMeta.account_id);
    } else if (myUser && myUser.int_account_id) {
      defaultSource = String(myUser.int_account_id);
    }

    const defaultTarget = targetUserOrAccount
      ? String(targetUserOrAccount.int_account_id || targetUserOrAccount.str_account_id || "")
      : "";

    setTransferForm({
      from_account_id: defaultSource,
      to_account_id: defaultTarget,
      amount: "",
      description: "",
    });
    setActiveDrawer("transfer");
  };

  const openRemoveDrawer = (targetUserOrAccount = null) => {
    let defaultSource = "";
    const myUser =
      usersList.find((u) => u.is_self) ||
      usersList.find((u) => u.str_username === currentUser?.str_username);

    if (requesterMeta?.account_id) {
      defaultSource = String(requesterMeta.account_id);
    } else if (myUser && myUser.int_account_id) {
      defaultSource = String(myUser.int_account_id);
    }

    const defaultTarget = targetUserOrAccount
      ? String(targetUserOrAccount.int_account_id || targetUserOrAccount.str_account_id || "")
      : "";

    setRemoveForm({
      target_account_id: defaultTarget,
      source_account_id: defaultSource,
      amount: "",
      return_to_source: true,
      description: "",
    });
    setActiveDrawer("remove");
  };

  // Selected accounts for live calculation preview in drawer
  const selectedAddTarget = useMemo(() => {
    if (!addForm.target_account_id) return null;
    return (
      usersList.find(
        (u) =>
          String(u.int_account_id) === String(addForm.target_account_id) ||
          String(u._id) === String(addForm.target_account_id)
      ) || null
    );
  }, [usersList, addForm.target_account_id]);

  const selectedTransferSource = useMemo(() => {
    if (!transferForm.from_account_id) return null;
    return (
      usersList.find((u) => String(u.int_account_id) === String(transferForm.from_account_id)) || null
    );
  }, [usersList, transferForm.from_account_id]);

  const selectedTransferTarget = useMemo(() => {
    if (!transferForm.to_account_id) return null;
    return (
      usersList.find((u) => String(u.int_account_id) === String(transferForm.to_account_id)) || null
    );
  }, [usersList, transferForm.to_account_id]);

  const selectedRemoveTarget = useMemo(() => {
    if (!removeForm.target_account_id) return null;
    return (
      usersList.find((u) => String(u.int_account_id) === String(removeForm.target_account_id)) || null
    );
  }, [usersList, removeForm.target_account_id]);

  const selectedRemoveSource = useMemo(() => {
    if (!removeForm.source_account_id) return null;
    return (
      usersList.find((u) => String(u.int_account_id) === String(removeForm.source_account_id)) || null
    );
  }, [usersList, removeForm.source_account_id]);

  // Submit Add Balance
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(addForm.amount);
    if (!addForm.target_account_id) {
      Swal.fire({ title: "Target Required", text: "Please select target account to credit.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }
    if (!amount || amount <= 0) {
      Swal.fire({ title: "Invalid Amount", text: "Please enter a positive credit amount.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }

    setSubmittingDrawer(true);
    try {
      const res = await ledgerService.addCredit({
        target_account_id: addForm.target_account_id,
        target_user_id: addForm.target_user_id,
        amount,
        description: addForm.description,
        expire_on: addForm.expire_on || undefined,
        performedBy: currentUser?.str_username,
        userRole,
      });

      if (res && res.success) {
        setActiveDrawer(null);
        Swal.fire({
          title: "Credits Added!",
          text: res.message || `Successfully added ${amount.toLocaleString()} credits.`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });
        await handleRefresh();
      } else {
        throw new Error(res?.error || "Failed to add credits");
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error", confirmButtonColor: "#3a57e8" });
    } finally {
      setSubmittingDrawer(false);
    }
  };

  // Submit Transfer Balance
  const handleTransferSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(transferForm.amount);
    if (!transferForm.from_account_id) {
      Swal.fire({ title: "Sender Required", text: "Please select the source account.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }
    if (!transferForm.to_account_id) {
      Swal.fire({ title: "Recipient Required", text: "Please select the destination account.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }
    if (String(transferForm.from_account_id) === String(transferForm.to_account_id)) {
      Swal.fire({ title: "Invalid Transfer", text: "Source and destination cannot be the same account.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }
    if (!amount || amount <= 0) {
      Swal.fire({ title: "Invalid Amount", text: "Please enter a positive transfer amount.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }

    // Check sender balance
    const sender = usersList.find((u) => String(u.int_account_id) === String(transferForm.from_account_id));
    if (sender && amount > sender.int_current_credit) {
      Swal.fire({
        title: "Insufficient Credits",
        text: `Sender only has ${sender.int_current_credit.toLocaleString()} credits available.`,
        icon: "warning",
        confirmButtonColor: "#3a57e8",
      });
      return;
    }

    setSubmittingDrawer(true);
    try {
      const res = await ledgerService.transferCredit({
        from_account_id: transferForm.from_account_id,
        to_account_id: transferForm.to_account_id,
        amount,
        description: transferForm.description,
        performedBy: currentUser?.str_username,
        userRole,
      });

      if (res && res.success) {
        setActiveDrawer(null);
        Swal.fire({
          title: "Transfer Successful!",
          text: res.message || `Successfully transferred ${amount.toLocaleString()} credits.`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });
        await handleRefresh();
      } else {
        throw new Error(res?.error || "Failed to transfer credits");
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error", confirmButtonColor: "#3a57e8" });
    } finally {
      setSubmittingDrawer(false);
    }
  };

  // Submit Remove Balance
  const handleRemoveSubmit = async (e) => {
    e.preventDefault();
    const amount = Number(removeForm.amount);
    if (!removeForm.target_account_id) {
      Swal.fire({ title: "Target Required", text: "Please select account to deduct credits from.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }
    if (!amount || amount <= 0) {
      Swal.fire({ title: "Invalid Amount", text: "Please enter a positive deduction amount.", icon: "warning", confirmButtonColor: "#3a57e8" });
      return;
    }

    // Check target balance
    const target = usersList.find((u) => String(u.int_account_id) === String(removeForm.target_account_id));
    if (target && amount > target.int_current_credit) {
      Swal.fire({
        title: "Deduction Exceeds Balance",
        text: `Account only has ${target.int_current_credit.toLocaleString()} credits available.`,
        icon: "warning",
        confirmButtonColor: "#3a57e8",
      });
      return;
    }

    setSubmittingDrawer(true);
    try {
      const res = await ledgerService.removeCredit({
        target_account_id: removeForm.target_account_id,
        source_account_id: removeForm.source_account_id,
        amount,
        return_to_source: removeForm.return_to_source,
        description: removeForm.description,
        performedBy: currentUser?.str_username,
        userRole,
      });

      if (res && res.success) {
        setActiveDrawer(null);
        Swal.fire({
          title: "Credits Removed!",
          text: res.message || `Successfully deducted ${amount.toLocaleString()} credits.`,
          icon: "success",
          confirmButtonColor: "#3a57e8",
        });
        await handleRefresh();
      } else {
        throw new Error(res?.error || "Failed to remove credits");
      }
    } catch (err) {
      Swal.fire({ title: "Error", text: err.message, icon: "error", confirmButtonColor: "#3a57e8" });
    } finally {
      setSubmittingDrawer(false);
    }
  };

  // Filtered Users List
  const filteredUsers = useMemo(() => {
    return usersList.filter((u) => {
      const q = userSearch.toLowerCase().trim();
      const matchSearch =
        !q ||
        (u.str_username || "").toLowerCase().includes(q) ||
        (u.str_name || "").toLowerCase().includes(q) ||
        (u.account_name || "").toLowerCase().includes(q) ||
        String(u.int_account_id || "").includes(q);

      const matchRole =
        roleFilter === "all" ||
        (u.str_role_name || "").toLowerCase() === roleFilter.toLowerCase();

      return matchSearch && matchRole;
    });
  }, [usersList, userSearch, roleFilter]);

  // Filtered Tenant Credits
  const filteredCredits = useMemo(() => {
    let result = credits;
    if (creditSearch) {
      const q = creditSearch.toLowerCase();
      result = result.filter(
        (c) =>
          (c.str_transaction_id || "").toLowerCase().includes(q) ||
          (c.str_source || "").toLowerCase().includes(q) ||
          (c.str_description || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [credits, creditSearch]);

  // Filtered Tenant Debits
  const filteredDebits = useMemo(() => {
    let result = debits;
    if (debitSearch) {
      const q = debitSearch.toLowerCase();
      result = result.filter(
        (d) =>
          (d.str_transaction_id || "").toLowerCase().includes(q) ||
          (d.str_source || "").toLowerCase().includes(q) ||
          (d.str_reference_id || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [debits, debitSearch]);

  // Self user identification from users list
  const selfUser = useMemo(() => {
    return (
      usersList.find((u) => u.is_self) ||
      usersList.find(
        (u) =>
          u.str_username &&
          currentUser?.str_username &&
          u.str_username.toLowerCase() === currentUser.str_username.toLowerCase()
      ) ||
      null
    );
  }, [usersList, currentUser]);

  // Calculate live user balance for header KPI
  const activeUserLiveBalance = useMemo(() => {
    if (selfUser && selfUser.int_current_credit !== undefined) {
      return selfUser.int_current_credit;
    }
    if (requesterMeta?.current_credit !== undefined) {
      return requesterMeta.current_credit;
    }
    return balanceSummary.currentBalance;
  }, [selfUser, requesterMeta, balanceSummary.currentBalance]);

  // Role Badge Renderer
  const renderRoleBadge = (roleName) => {
    const r = (roleName || "user").toLowerCase();
    if (r.includes("superadmin") || r.includes("super admin")) {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
          SuperAdmin
        </span>
      );
    }
    if (r === "admin") {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
          Admin
        </span>
      );
    }
    if (r === "reseller") {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
          Reseller
        </span>
      );
    }
    if (r === "account") {
      return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
          Account
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700 dark:bg-gray-800 dark:text-gray-300 border border-slate-200 dark:border-gray-700">
        User
      </span>
    );
  };

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto min-h-screen">
      {/* ─── Header ─── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-[#151824] p-6 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#232d42] dark:text-white tracking-tight">
              Wallet & Ledgers
            </h1>
            {renderRoleBadge(userRole)}
          </div>
          <p className="text-xs sm:text-sm text-[#8a92a6] dark:text-gray-400 mt-1">
            Real-time multi-role credit balances, transfers, deductions, and financial audit logs.
          </p>
        </div>

        {/* Action Buttons based on Role Permissions */}
        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg bg-gray-100 dark:bg-gray-800 text-[#232d42] dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            title="Refresh All Ledgers"
          >
            <ArrowPathIcon className="w-4 h-4 text-[#8a92a6]" />
            <span>Refresh</span>
          </button>

          {/* SuperAdmin & Admin: Add Balance */}
          {permissions.canAdd && (
            <button
              onClick={() => openAddDrawer()}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm cursor-pointer transition-colors"
              title="Add Credits (SuperAdmin / Admin)"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add Balance</span>
            </button>
          )}

          {/* SuperAdmin, Admin, Reseller, Account: Transfer Credits */}
          {permissions.canTransfer && (
            <button
              onClick={() => openTransferDrawer()}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-[#3a57e8] hover:bg-[#2e47d4] text-white shadow-sm cursor-pointer transition-colors"
              title="Transfer Credits"
            >
              <ArrowsRightLeftIcon className="w-4 h-4" />
              <span>Transfer Balance</span>
            </button>
          )}

          {/* SuperAdmin, Admin, Reseller: Remove Balance */}
          {permissions.canRemove && (
            <button
              onClick={() => openRemoveDrawer()}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg bg-rose-600 hover:bg-rose-700 text-white shadow-sm cursor-pointer transition-colors"
              title="Remove Balance"
            >
              <MinusIcon className="w-4 h-4" />
              <span>Remove Balance</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── KPI Stat Cards ─── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Available Balance */}
        <div className="bg-white dark:bg-[#151824] rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8a92a6] dark:text-gray-400">
            Available Balance
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#3a57e8] font-mono mt-1">
            {Number(activeUserLiveBalance || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-[#8a92a6] mt-1">
            {selfUser
              ? `${selfUser.account_name || "Platform Account"}${selfUser.str_username ? ` (@${selfUser.str_username})` : ""}`
              : (requesterMeta?.account_name || "Platform Credits")}
          </div>
        </div>

        {/* Card 2: Total Recharges */}
        <div className="bg-white dark:bg-[#151824] rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8a92a6] dark:text-gray-400">
            Total Recharges
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 font-mono mt-1">
            +{Number(balanceSummary.totalCredits || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-[#8a92a6] mt-1">Incoming credits</div>
        </div>

        {/* Card 3: Total Deductions */}
        <div className="bg-white dark:bg-[#151824] rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8a92a6] dark:text-gray-400">
            Total Deductions
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-rose-600 font-mono mt-1">
            -{Number(balanceSummary.totalDebits || 0).toLocaleString()}
          </div>
          <div className="text-[11px] text-[#8a92a6] mt-1">Broadcast usage</div>
        </div>

        {/* Card 4: Accounts in Scope */}
        <div className="bg-white dark:bg-[#151824] rounded-xl p-5 border border-gray-200 dark:border-gray-800 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8a92a6] dark:text-gray-400">
            {userRole === "reseller" ? "Created Clients" : "Active Users"}
          </div>
          <div className="text-2xl sm:text-3xl font-extrabold text-[#232d42] dark:text-white font-mono mt-1">
            {usersList.length}
          </div>
          <div className="text-[11px] text-[#8a92a6] mt-1">
            {userRole === "superadmin" ? "All roles platform-wide" : "Within your role boundary"}
          </div>
        </div>
      </div>

      {/* ─── Navigation Tabs ─── */}
      <div className="border-b border-gray-200 dark:border-gray-800 flex flex-wrap items-center gap-2">
        {/* Tab 1: Users & Balances (for administration roles) */}
        {["superadmin", "admin", "reseller", "account"].includes(userRole) && (
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-3 px-4 text-xs font-bold transition-colors cursor-pointer border-b-2 ${
              activeTab === "users"
                ? "border-[#3a57e8] text-[#3a57e8]"
                : "border-transparent text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white"
            }`}
          >
            👥 Users & Credit Balances ({usersList.length})
          </button>
        )}

        {/* Tab 2: Audit Transactions (Credit, Debit, Transfer) */}
        {["superadmin", "account", "admin", "reseller"].includes(userRole) && (
          <button
            onClick={() => setActiveTab("transactions")}
            className={`pb-3 px-4 text-xs font-bold transition-colors cursor-pointer border-b-2 ${
              activeTab === "transactions"
                ? "border-[#3a57e8] text-[#3a57e8]"
                : "border-transparent text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white"
            }`}
          >
            📋 All Transactions Audit ({totalTxns || transactions.length})
          </button>
        )}

        {/* Tab 3: Tenant Credit Top-ups */}
        <button
          onClick={() => setActiveTab("credits")}
          className={`pb-3 px-4 text-xs font-bold transition-colors cursor-pointer border-b-2 ${
            activeTab === "credits"
              ? "border-[#3a57e8] text-[#3a57e8]"
              : "border-transparent text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white"
          }`}
        >
          📥 Credit Top-Ups
        </button>

        {/* Tab 4: Tenant Usage Deductions */}
        <button
          onClick={() => setActiveTab("debits")}
          className={`pb-3 px-4 text-xs font-bold transition-colors cursor-pointer border-b-2 ${
            activeTab === "debits"
              ? "border-[#3a57e8] text-[#3a57e8]"
              : "border-transparent text-[#8a92a6] hover:text-[#232d42] dark:hover:text-white"
          }`}
        >
          📤 Usage Deductions
        </button>
      </div>

      {/* ─── TAB 1: USERS & CREDIT BALANCES ─── */}
      {activeTab === "users" && (
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden space-y-4 p-5">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search user, company, or account ID..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#3a57e8]"
              />
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <label className="text-xs text-gray-500 font-medium whitespace-nowrap">Filter Role:</label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="text-xs px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
              >
                <option value="all">All Roles</option>
                {userRole === "superadmin" && <option value="superadmin">SuperAdmin</option>}
                <option value="admin">Admin</option>
                <option value="reseller">Reseller</option>
                <option value="account">Account</option>
                <option value="user">User</option>
              </select>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[#8a92a6] uppercase tracking-wider font-semibold">
                  <th className="py-3 px-4">User Details</th>
                  <th className="py-3 px-4">Role</th>
                  <th className="py-3 px-4">Account / Company</th>
                  <th className="py-3 px-4 text-right">Available Credits</th>
                  <th className="py-3 px-4">Expiry Date</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {loadingUsers ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-500">
                      Loading user accounts and balances...
                    </td>
                  </tr>
                ) : filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-400">
                      No user accounts found matching current scope or search filter.
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((u) => (
                    <tr
                      key={String(u._id)}
                      className={`hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors ${
                        u.is_self ? "bg-blue-50/30 dark:bg-blue-950/20" : ""
                      }`}
                    >
                      {/* User */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-[#3a57e8]/10 text-[#3a57e8] flex items-center justify-center font-bold text-xs uppercase flex-shrink-0">
                            {(u.str_username || "U")[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                              <span>@{u.str_username}</span>
                              {u.is_self && (
                                <span className="text-[10px] font-bold text-[#3a57e8] bg-blue-100 dark:bg-blue-950 px-1.5 py-0.2 rounded">
                                  YOU
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-500">{u.str_name || u.str_email}</div>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td className="py-3.5 px-4">{renderRoleBadge(u.str_role_name)}</td>

                      {/* Account */}
                      <td className="py-3.5 px-4">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{u.account_name}</div>
                        <div className="text-[11px] font-mono text-gray-400">
                          {u.int_account_id ? `ACC${String(u.int_account_id).padStart(6, "0")}` : "No Account ID"}
                        </div>
                      </td>

                      {/* Credits */}
                      <td className="py-3.5 px-4 text-right">
                        <span className="font-bold text-sm font-mono text-emerald-600 dark:text-emerald-400">
                          {Number(u.int_current_credit || 0).toLocaleString()}
                        </span>
                        <span className="text-[10px] text-gray-400 block font-normal">credits</span>
                      </td>

                      {/* Expiry */}
                      <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                        {u.dttm_credit_expire_on
                          ? new Date(u.dttm_credit_expire_on).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "Never (No Expiry)"}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 text-center">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            u.str_status === "ACTIVE"
                              ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                          }`}
                        >
                          {u.str_status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* SuperAdmin & Admin: Add */}
                          {permissions.canAdd && (
                            <button
                              onClick={() => openAddDrawer(u)}
                              className="px-2.5 py-1 text-[11px] font-bold rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 cursor-pointer transition-colors"
                              title="Add Credits (SuperAdmin / Admin)"
                            >
                              + Add
                            </button>
                          )}

                          {/* Transfer (SuperAdmin, Admin, Reseller, Account) */}
                          {permissions.canTransfer && (
                            <button
                              onClick={() => openTransferDrawer(u)}
                              className="px-2.5 py-1 text-[11px] font-bold rounded bg-blue-50 text-[#3a57e8] hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 cursor-pointer transition-colors"
                              title="Transfer Credits"
                            >
                              ⇄ Transfer
                            </button>
                          )}

                          {/* Remove (SuperAdmin, Admin, Reseller) */}
                          {permissions.canRemove && !u.str_role_name?.toLowerCase().includes("superadmin") && (
                            <button
                              onClick={() => openRemoveDrawer(u)}
                              className="px-2.5 py-1 text-[11px] font-bold rounded bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300 cursor-pointer transition-colors"
                              title="Remove Credits"
                            >
                              - Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB 2: AUDIT TRANSACTIONS (Central Ledger) ─── */}
      {activeTab === "transactions" && (
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden space-y-4 p-5">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search transaction ID, user, actor..."
                value={txnSearch}
                onChange={(e) => {
                  setTxnSearch(e.target.value);
                  setTxnPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#3a57e8]"
              />
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 font-medium whitespace-nowrap">Transaction Type:</label>
              <select
                value={txnTypeFilter}
                onChange={(e) => {
                  setTxnTypeFilter(e.target.value);
                  setTxnPage(1);
                }}
                className="text-xs px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none"
              >
                <option value="all">All Types</option>
                <option value="ADD">ADD (SuperAdmin Recharge)</option>
                <option value="TRANSFER">TRANSFER (Balance Transfer)</option>
                <option value="REMOVE">REMOVE (Balance Deduction)</option>
                <option value="USAGE">USAGE (Campaign / API)</option>
              </select>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[#8a92a6] uppercase tracking-wider font-semibold">
                  <th className="py-3 px-4">Transaction ID</th>
                  <th className="py-3 px-4">Timestamp</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                  <th className="py-3 px-4">From Account</th>
                  <th className="py-3 px-4">To Account</th>
                  <th className="py-3 px-4">Actor</th>
                  <th className="py-3 px-4">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {loadingTransactions ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      Loading audit transaction records...
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-400">
                      No financial transactions found.
                    </td>
                  </tr>
                ) : (
                  transactions.map((t) => (
                    <tr key={t.str_transaction_id || String(t._id)} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-mono font-medium text-gray-800 dark:text-gray-200">
                        {t.str_transaction_id}
                      </td>
                      <td className="py-3 px-4 text-gray-500 whitespace-nowrap">
                        {t.dttm_created_on
                          ? new Date(t.dttm_created_on).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            t.str_type === "ADD"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                              : t.str_type === "TRANSFER"
                              ? "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
                              : t.str_type === "REMOVE"
                              ? "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300"
                              : "bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300"
                          }`}
                        >
                          {t.str_type}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold">
                        <span
                          className={
                            t.str_type === "ADD" || t.str_type === "TRANSFER"
                              ? "text-emerald-600"
                              : "text-rose-600"
                          }
                        >
                          {t.str_type === "ADD" ? "+" : ""}
                          {Number(t.dbl_amount || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        {t.str_from_company || t.str_from_username ? (
                          <span>
                            {t.str_from_company || `@${t.str_from_username}`}
                            {t.int_from_account_id ? ` (ACC${t.int_from_account_id})` : ""}
                          </span>
                        ) : (
                          <span className="text-gray-400">System / Mint</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600 dark:text-gray-300">
                        {t.str_to_company || t.str_to_username ? (
                          <span>
                            {t.str_to_company || `@${t.str_to_username}`}
                            {t.int_to_account_id ? ` (ACC${t.int_to_account_id})` : ""}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-700 dark:text-gray-300">
                        <div className="font-semibold text-[11px]">@{t.str_performed_by || "system"}</div>
                        <div className="text-[10px] text-gray-400 uppercase">{t.str_performed_role || ""}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-500 max-w-xs truncate" title={t.str_description}>
                        {t.str_description || "—"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-100 dark:border-gray-800">
            <div>
              Showing {transactions.length} of {totalTxns} records
            </div>
            <div className="flex items-center gap-2">
              <button
                disabled={txnPage <= 1}
                onClick={() => setTxnPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 cursor-pointer"
              >
                <ChevronLeftIcon className="w-3.5 h-3.5" />
              </button>
              <span>Page {txnPage}</span>
              <button
                disabled={txnPage * txnLimit >= totalTxns}
                onClick={() => setTxnPage((p) => p + 1)}
                className="p-1.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 cursor-pointer"
              >
                <ChevronRightIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── TAB 3: TENANT CREDIT TOP-UPS (tbl_credit_ledger) ─── */}
      {activeTab === "credits" && (
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search transaction ID or source..."
                value={creditSearch}
                onChange={(e) => setCreditSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[#8a92a6] uppercase font-semibold">
                  <th className="py-3 px-4">Transaction ID</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Source</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                  <th className="py-3 px-4 text-right">New Balance</th>
                  <th className="py-3 px-4">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {loadingCredits ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      Loading credits...
                    </td>
                  </tr>
                ) : filteredCredits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400">
                      No credit transactions recorded in tenant database.
                    </td>
                  </tr>
                ) : (
                  filteredCredits.map((c) => (
                    <tr key={c.str_transaction_id || String(c._id)} className="hover:bg-gray-50/80">
                      <td className="py-3 px-4 font-mono font-medium">{c.str_transaction_id}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {c.dttm_created_on ? new Date(c.dttm_created_on).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 px-4">{c.str_source || "Admin Top-up"}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-emerald-600">
                        +{Number(c.dbl_amount || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-medium text-gray-700 dark:text-gray-300">
                        {Number(c.dbl_new_balance || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-gray-500">{c.str_description || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── TAB 4: TENANT USAGE DEDUCTIONS (tbl_debit_ledger) ─── */}
      {activeTab === "debits" && (
        <div className="bg-white dark:bg-[#151824] rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search deduction or reference..."
                value={debitSearch}
                onChange={(e) => setDebitSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white"
              />
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[#8a92a6] uppercase font-semibold">
                  <th className="py-3 px-4">Transaction ID</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Source / Type</th>
                  <th className="py-3 px-4 text-right">Deducted Amount</th>
                  <th className="py-3 px-4 text-right">Remaining Balance</th>
                  <th className="py-3 px-4">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {loadingDebits ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500">
                      Loading deductions...
                    </td>
                  </tr>
                ) : filteredDebits.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-400">
                      No usage deductions recorded in tenant database.
                    </td>
                  </tr>
                ) : (
                  filteredDebits.map((d) => (
                    <tr key={d.str_transaction_id || String(d._id)} className="hover:bg-gray-50/80">
                      <td className="py-3 px-4 font-mono font-medium">{d.str_transaction_id}</td>
                      <td className="py-3 px-4 text-gray-500">
                        {d.dttm_created_on ? new Date(d.dttm_created_on).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 px-4">{d.str_source || "Campaign"}</td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-rose-600">
                        -{Number(d.dbl_amount || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-medium text-gray-700 dark:text-gray-300">
                        {Number(d.dbl_new_balance || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-gray-500">{d.str_description || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── PORTAL: RIGHT SLIDE-OVER DRAWER (MOUNTED DIRECTLY TO DOCUMENT BODY) ─── */}
      {typeof document !== "undefined" &&
        createPortal(
          <>
            {activeDrawer && (
              <div
                className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-xs transition-opacity duration-300 animate-in fade-in"
                onClick={closeDrawer}
                aria-hidden="true"
              />
            )}

            {/* ─── RIGHT SLIDE-OVER DRAWER PANEL ─── */}
            <div
              className={`fixed inset-y-0 right-0 z-[9999] w-full sm:w-[500px] md:w-[540px] bg-white dark:bg-[#151824] shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-800 transform transition-transform duration-300 ease-in-out ${
                activeDrawer ? "translate-x-0" : "translate-x-full pointer-events-none"
              }`}
            >
              {activeDrawer && (
                <>
                  {/* Drawer Header */}
                  <div className="relative border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#151824] px-6 py-5 flex-shrink-0">
                    {/* Top Accent Strip */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1.5 ${
                        activeDrawer === "add"
                          ? "bg-emerald-500"
                          : activeDrawer === "transfer"
                          ? "bg-[#3a57e8]"
                          : "bg-rose-500"
                      }`}
                    />

                    <div className="flex items-center justify-between gap-3 pt-1">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div
                          className={`w-11 h-11 rounded-xl flex items-center justify-center font-bold shadow-xs flex-shrink-0 ${
                            activeDrawer === "add"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                              : activeDrawer === "transfer"
                              ? "bg-blue-100 text-[#3a57e8] dark:bg-blue-950/60 dark:text-blue-300"
                              : "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300"
                          }`}
                        >
                          {activeDrawer === "add" && <PlusIcon className="w-5 h-5" />}
                          {activeDrawer === "transfer" && <ArrowsRightLeftIcon className="w-5 h-5" />}
                          {activeDrawer === "remove" && <MinusIcon className="w-5 h-5" />}
                        </div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-extrabold text-base text-gray-900 dark:text-white truncate">
                              {activeDrawer === "add" && "Add Wallet Credits"}
                              {activeDrawer === "transfer" && "Transfer Balance"}
                              {activeDrawer === "remove" && "Remove / Deduct Credits"}
                            </h3>
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                activeDrawer === "add"
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                                  : activeDrawer === "transfer"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                                  : "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
                              }`}
                            >
                              {activeDrawer === "add" && (userRole === "admin" ? "Admin Direct Add" : "SuperAdmin Direct Add")}
                              {activeDrawer === "transfer" && "Direct Transfer"}
                              {activeDrawer === "remove" && "Balance Deduction"}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                            {activeDrawer === "add" && "Directly credit any account balance without deduction."}
                            {activeDrawer === "transfer" && "Move credits between registered accounts."}
                            {activeDrawer === "remove" && "Deduct credits or reverse mistaken allocations."}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={closeDrawer}
                        disabled={submittingDrawer}
                        className="p-2 rounded-xl text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer flex-shrink-0"
                        title="Close (Esc)"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

            {/* ─── DRAWER BODY: ADD CREDITS ─── */}
            {activeDrawer === "add" && (
              <form onSubmit={handleAddSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {/* Target Account */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                      <span>Target Account <span className="text-rose-500">*</span></span>
                      {selectedAddTarget && (
                        <span className="text-[11px] font-mono font-medium text-emerald-600 dark:text-emerald-400">
                          Current Bal: {Number(selectedAddTarget.int_current_credit || 0).toLocaleString()}
                        </span>
                      )}
                    </label>
                    <select
                      required
                      value={addForm.target_account_id}
                      onChange={(e) => {
                        const val = e.target.value;
                        const matched = usersList.find(
                          (u) => String(u.int_account_id) === String(val) || String(u._id) === String(val)
                        );
                        setAddForm({
                          ...addForm,
                          target_account_id: val,
                          target_user_id: matched ? String(matched._id || matched.str_username) : "",
                        });
                      }}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    >
                      <option value="">-- Select Target Account --</option>
                      {usersList.map((u) => (
                        <option key={String(u._id)} value={u.int_account_id}>
                          {u.is_self ? "⭐ [YOUR ACCOUNT] " : ""}
                          {u.account_name} (@{u.str_username}) - Bal: {Number(u.int_current_credit || 0).toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Credit Amount */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Credit Amount <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      step="1"
                      placeholder="e.g. 5000"
                      value={addForm.amount}
                      onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })}
                      className="w-full text-sm font-mono px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />

                    {/* Quick increment chips */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[500, 1000, 5000, 10000, 50000, 100000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() =>
                            setAddForm({
                              ...addForm,
                              amount: String((Number(addForm.amount) || 0) + preset),
                            })
                          }
                          className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800 cursor-pointer transition-colors"
                        >
                          +{preset.toLocaleString()}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setAddForm({ ...addForm, amount: "" })}
                        className="px-2 py-1 text-[11px] font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700 cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Live Calculation Preview Card */}
                  {selectedAddTarget && Number(addForm.amount) > 0 && (
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-2xl border border-emerald-200 dark:border-emerald-900/60 text-xs space-y-2">
                      <div className="flex items-center justify-between font-bold text-emerald-900 dark:text-emerald-200">
                        <span>Balance Projection</span>
                        <span className="font-mono text-emerald-600 dark:text-emerald-400">
                          {selectedAddTarget.account_name} (@{selectedAddTarget.str_username})
                        </span>
                      </div>
                      <div className="space-y-1 text-gray-600 dark:text-gray-300 text-[11px] pt-1 border-t border-emerald-100 dark:border-emerald-900/40">
                        <div className="flex justify-between">
                          <span>Current Balance:</span>
                          <span className="font-mono font-semibold">
                            {Number(selectedAddTarget.int_current_credit || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-emerald-700 dark:text-emerald-400 font-semibold">
                          <span>Credits Added:</span>
                          <span className="font-mono">+{Number(addForm.amount).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-emerald-200 dark:border-emerald-900 font-bold text-gray-900 dark:text-white">
                          <span>Resulting Balance:</span>
                          <span className="font-mono text-emerald-600 dark:text-emerald-300 text-sm">
                            {(
                              Number(selectedAddTarget.int_current_credit || 0) + Number(addForm.amount)
                            ).toLocaleString()}{" "}
                            credits
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Credit Expiration Date (Optional) */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Credit Expiration Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={addForm.expire_on}
                      onChange={(e) => setAddForm({ ...addForm, expire_on: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                    <span className="text-[10px] text-gray-400 mt-1 block">
                      Leave empty for permanent credits that never expire.
                    </span>
                  </div>

                  {/* Description / Reference Note */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Description / Reference Note
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Monthly allocation / Promo recharge"
                      value={addForm.description}
                      onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Drawer Footer */}
                <div className="sticky bottom-0 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-md px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    disabled={submittingDrawer}
                    className="px-4 py-2.5 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submittingDrawer || !addForm.target_account_id || !addForm.amount}
                    className="px-6 py-2.5 text-xs font-bold rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-md disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    {submittingDrawer ? "Adding Credits..." : "Confirm & Add"}
                  </button>
                </div>
              </form>
            )}

            {/* ─── DRAWER BODY: TRANSFER CREDITS ─── */}
            {activeDrawer === "transfer" && (
              <form onSubmit={handleTransferSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {/* Sender Account */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                      <span>From Account (Sender) <span className="text-rose-500">*</span></span>
                      {selectedTransferSource && (
                        <span className="text-[11px] font-mono font-medium text-[#3a57e8] dark:text-blue-400">
                          Available: {Number(selectedTransferSource.int_current_credit || 0).toLocaleString()}
                        </span>
                      )}
                    </label>
                    <select
                      required
                      value={transferForm.from_account_id}
                      onChange={(e) => setTransferForm({ ...transferForm, from_account_id: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#3a57e8] focus:outline-none"
                    >
                      <option value="">-- Select Source Account --</option>
                      {usersList.map((u) => (
                        <option key={String(u._id)} value={u.int_account_id}>
                          {u.is_self ? "⭐ [YOU] " : ""}
                          {u.account_name} (@{u.str_username}) - Bal: {Number(u.int_current_credit || 0).toLocaleString()}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Recipient Account */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                      <span>To Account (Recipient) <span className="text-rose-500">*</span></span>
                      {selectedTransferTarget && (
                        <span className="text-[11px] font-mono font-medium text-emerald-600 dark:text-emerald-400">
                          Current Bal: {Number(selectedTransferTarget.int_current_credit || 0).toLocaleString()}
                        </span>
                      )}
                    </label>
                    <select
                      required
                      value={transferForm.to_account_id}
                      onChange={(e) => setTransferForm({ ...transferForm, to_account_id: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#3a57e8] focus:outline-none"
                    >
                      <option value="">-- Select Recipient Account --</option>
                      {usersList
                        .filter((u) => String(u.int_account_id) !== String(transferForm.from_account_id))
                        .map((u) => (
                          <option key={String(u._id)} value={u.int_account_id}>
                            {u.account_name} (@{u.str_username}) - Bal: {Number(u.int_current_credit || 0).toLocaleString()}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Transfer Amount */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Credits to Transfer <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      step="1"
                      placeholder="e.g. 1000"
                      value={transferForm.amount}
                      onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                      className="w-full text-sm font-mono px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#3a57e8] focus:outline-none"
                    />

                    {/* Quick increment chips */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[100, 500, 1000, 5000, 10000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() =>
                            setTransferForm({
                              ...transferForm,
                              amount: String((Number(transferForm.amount) || 0) + preset),
                            })
                          }
                          className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-blue-50 text-[#3a57e8] hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800 cursor-pointer transition-colors"
                        >
                          +{preset.toLocaleString()}
                        </button>
                      ))}
                      {selectedTransferSource && (
                        <button
                          type="button"
                          onClick={() =>
                            setTransferForm({
                              ...transferForm,
                              amount: String(selectedTransferSource.int_current_credit || 0),
                            })
                          }
                          className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800 cursor-pointer"
                        >
                          Max
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setTransferForm({ ...transferForm, amount: "" })}
                        className="px-2 py-1 text-[11px] font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700 cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Insufficient Funds Warning */}
                  {selectedTransferSource &&
                    Number(transferForm.amount) > (selectedTransferSource.int_current_credit || 0) && (
                      <div className="p-3 bg-rose-50 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300">
                        ⚠️ Sender account only has{" "}
                        <span className="font-mono font-bold">
                          {Number(selectedTransferSource.int_current_credit || 0).toLocaleString()}
                        </span>{" "}
                        credits available.
                      </div>
                    )}

                  {/* Live Calculation Preview Card */}
                  {selectedTransferSource && selectedTransferTarget && Number(transferForm.amount) > 0 && (
                    <div className="p-4 bg-blue-50/70 dark:bg-blue-950/30 rounded-2xl border border-blue-200 dark:border-blue-900/60 text-xs space-y-2.5">
                      <div className="font-bold text-[#3a57e8] dark:text-blue-300">
                        Transfer Simulation Preview
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-[11px] pt-1 border-t border-blue-100 dark:border-blue-900/40">
                        {/* Sender preview */}
                        <div className="p-2.5 rounded-xl bg-white/70 dark:bg-gray-800/60">
                          <span className="font-semibold text-gray-700 dark:text-gray-300 block truncate">
                            Sender: {selectedTransferSource.account_name}
                          </span>
                          <div className="mt-1 space-y-0.5 text-gray-500 dark:text-gray-400">
                            <div>Bal: {Number(selectedTransferSource.int_current_credit || 0).toLocaleString()}</div>
                            <div className="text-rose-600 font-semibold font-mono">
                              -{Number(transferForm.amount).toLocaleString()}
                            </div>
                            <div className="font-bold text-gray-900 dark:text-white pt-0.5 border-t border-gray-200 dark:border-gray-700 font-mono">
                              = {(
                                Number(selectedTransferSource.int_current_credit || 0) - Number(transferForm.amount)
                              ).toLocaleString()}
                            </div>
                          </div>
                        </div>

                        {/* Recipient preview */}
                        <div className="p-2.5 rounded-xl bg-white/70 dark:bg-gray-800/60">
                          <span className="font-semibold text-gray-700 dark:text-gray-300 block truncate">
                            Recipient: {selectedTransferTarget.account_name}
                          </span>
                          <div className="mt-1 space-y-0.5 text-gray-500 dark:text-gray-400">
                            <div>Bal: {Number(selectedTransferTarget.int_current_credit || 0).toLocaleString()}</div>
                            <div className="text-emerald-600 font-semibold font-mono">
                              +{Number(transferForm.amount).toLocaleString()}
                            </div>
                            <div className="font-bold text-gray-900 dark:text-white pt-0.5 border-t border-gray-200 dark:border-gray-700 font-mono">
                              = {(
                                Number(selectedTransferTarget.int_current_credit || 0) + Number(transferForm.amount)
                              ).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Transfer Note */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Transfer Note / Reference
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Campaign budget allocation"
                      value={transferForm.description}
                      onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#3a57e8] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Drawer Footer */}
                <div className="sticky bottom-0 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-md px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    disabled={submittingDrawer}
                    className="px-4 py-2.5 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      submittingDrawer ||
                      !transferForm.from_account_id ||
                      !transferForm.to_account_id ||
                      !transferForm.amount ||
                      (selectedTransferSource &&
                        Number(transferForm.amount) > (selectedTransferSource.int_current_credit || 0))
                    }
                    className="px-6 py-2.5 text-xs font-bold rounded-xl bg-[#3a57e8] hover:bg-[#2e47d4] text-white shadow-md disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    {submittingDrawer ? "Transferring..." : "Confirm & Transfer"}
                  </button>
                </div>
              </form>
            )}

            {/* ─── DRAWER BODY: REMOVE / DEDUCT CREDITS ─── */}
            {activeDrawer === "remove" && (
              <form onSubmit={handleRemoveSubmit} className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  {/* Target Account */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                      <span>Target Account (Deduct from) <span className="text-rose-500">*</span></span>
                      {selectedRemoveTarget && (
                        <span className="text-[11px] font-mono font-medium text-rose-600 dark:text-rose-400">
                          Current Bal: {Number(selectedRemoveTarget.int_current_credit || 0).toLocaleString()}
                        </span>
                      )}
                    </label>
                    <select
                      required
                      value={removeForm.target_account_id}
                      onChange={(e) => setRemoveForm({ ...removeForm, target_account_id: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:outline-none"
                    >
                      <option value="">-- Select Account to Deduct From --</option>
                      {usersList
                        .filter((u) => !u.str_role_name?.toLowerCase().includes("superadmin"))
                        .map((u) => (
                          <option key={String(u._id)} value={u.int_account_id}>
                            {u.account_name} (@{u.str_username}) - Bal: {Number(u.int_current_credit || 0).toLocaleString()}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Deduct Amount */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Credits to Deduct <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      step="1"
                      placeholder="e.g. 500"
                      value={removeForm.amount}
                      onChange={(e) => setRemoveForm({ ...removeForm, amount: e.target.value })}
                      className="w-full text-sm font-mono px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:outline-none"
                    />

                    {/* Quick increment chips */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[100, 500, 1000, 5000].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() =>
                            setRemoveForm({
                              ...removeForm,
                              amount: String((Number(removeForm.amount) || 0) + preset),
                            })
                          }
                          className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-rose-50 text-rose-700 hover:bg-rose-100 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-200 dark:border-rose-800 cursor-pointer transition-colors"
                        >
                          +{preset.toLocaleString()}
                        </button>
                      ))}
                      {selectedRemoveTarget && (
                        <button
                          type="button"
                          onClick={() =>
                            setRemoveForm({
                              ...removeForm,
                              amount: String(selectedRemoveTarget.int_current_credit || 0),
                            })
                          }
                          className="px-2.5 py-1 text-[11px] font-medium rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 dark:border-amber-800 cursor-pointer"
                        >
                          All Bal
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setRemoveForm({ ...removeForm, amount: "" })}
                        className="px-2 py-1 text-[11px] font-medium rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-700 cursor-pointer"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  {/* Deduction Exceeds Balance Warning */}
                  {selectedRemoveTarget &&
                    Number(removeForm.amount) > (selectedRemoveTarget.int_current_credit || 0) && (
                      <div className="p-3 bg-rose-50 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-900/60 text-xs text-rose-700 dark:text-rose-300">
                        ⚠️ Deduction exceeds balance! Account only has{" "}
                        <span className="font-mono font-bold">
                          {Number(selectedRemoveTarget.int_current_credit || 0).toLocaleString()}
                        </span>{" "}
                        credits.
                      </div>
                    )}

                  {/* Return to Source Account Checkbox */}
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="checkbox"
                      id="return_to_source_drawer"
                      checked={removeForm.return_to_source}
                      onChange={(e) => setRemoveForm({ ...removeForm, return_to_source: e.target.checked })}
                      className="w-4 h-4 rounded text-[#3a57e8] border-gray-300 focus:ring-[#3a57e8]"
                    />
                    <label
                      htmlFor="return_to_source_drawer"
                      className="text-xs text-gray-700 dark:text-gray-300 font-medium"
                    >
                      Return deducted credits back to my balance pool
                    </label>
                  </div>

                  {/* Live Calculation Preview Card */}
                  {selectedRemoveTarget && Number(removeForm.amount) > 0 && (
                    <div className="p-4 bg-rose-50/70 dark:bg-rose-950/30 rounded-2xl border border-rose-200 dark:border-rose-900/60 text-xs space-y-2">
                      <div className="flex items-center justify-between font-bold text-rose-900 dark:text-rose-200">
                        <span>Deduction Impact Preview</span>
                        <span className="font-mono text-rose-600 dark:text-rose-400">
                          {selectedRemoveTarget.account_name} (@{selectedRemoveTarget.str_username})
                        </span>
                      </div>
                      <div className="space-y-1 text-gray-600 dark:text-gray-300 text-[11px] pt-1 border-t border-rose-100 dark:border-rose-900/40">
                        <div className="flex justify-between">
                          <span>Target Current Balance:</span>
                          <span className="font-mono font-semibold">
                            {Number(selectedRemoveTarget.int_current_credit || 0).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-rose-600 dark:text-rose-400 font-semibold">
                          <span>Deducted Amount:</span>
                          <span className="font-mono">-{Number(removeForm.amount).toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between pt-1 border-t border-rose-200 dark:border-rose-900 font-bold text-gray-900 dark:text-white">
                          <span>Remaining Balance:</span>
                          <span className="font-mono text-rose-600 dark:text-rose-300 text-sm">
                            {Math.max(
                              0,
                              Number(selectedRemoveTarget.int_current_credit || 0) - Number(removeForm.amount)
                            ).toLocaleString()}{" "}
                            credits
                          </span>
                        </div>
                        {removeForm.return_to_source && selectedRemoveSource && (
                          <div className="flex justify-between text-blue-600 dark:text-blue-400 font-semibold pt-1 border-t border-rose-200/50 dark:border-rose-900/30">
                            <span>Returned to My Pool:</span>
                            <span className="font-mono">
                              +{Number(removeForm.amount).toLocaleString()} credits
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                      Deduction Reason / Note
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Allocation reversal / Audit correction"
                      value={removeForm.description}
                      onChange={(e) => setRemoveForm({ ...removeForm, description: e.target.value })}
                      className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-rose-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Drawer Footer */}
                <div className="sticky bottom-0 bg-gray-50/95 dark:bg-gray-800/95 backdrop-blur-md px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeDrawer}
                    disabled={submittingDrawer}
                    className="px-4 py-2.5 text-xs font-semibold rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      submittingDrawer ||
                      !removeForm.target_account_id ||
                      !removeForm.amount ||
                      (selectedRemoveTarget &&
                        Number(removeForm.amount) > (selectedRemoveTarget.int_current_credit || 0))
                    }
                    className="px-6 py-2.5 text-xs font-bold rounded-xl bg-rose-600 hover:bg-rose-700 text-white shadow-md disabled:opacity-50 cursor-pointer transition-colors"
                  >
                    {submittingDrawer ? "Removing..." : "Confirm & Deduct"}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </>,
    document.body
  )}
    </div>
  );
}
